// Checklist definitions for the order flow — SINGLE SOURCE OF TRUTH.
// The backend has no copy: it stores whatever keys the client confirms, so
// adding/changing checklist items (including device types below) happens in
// this file and the locale files only.
//
// Two checklists:
//   - the checklist for every move into Färdig: one fixed list for every
//     order, with no device-type sections
//   - the edit-order progress checklist (REQ-05): the general list below is
//     ALWAYS shown, and device-type-specific items (REQ-09) are added on top
//     of it. Device types with no defined items are flagged to support.

// Items for every move into Färdig (complete). All of them must be ticked.
// labels: order_log.completion_checklist_items.<key>
export const COMPLETION_CHECKLIST_KEYS = [
  "gps",
  "config",
  "notifications_on",
  "test_alarm",
  "tunnel_to_lc",
  "sms_mail",
  "external_responder",
  "additional_services",
  "display_connected",
  "contract_number",
  "contract_start",
  "sc_user",
  "accessories_packed",
  "quick_guide_packed",
  "control_ok",
];

// General items for the edit-order progress checklist (REQ-05)
export const PROGRESS_CHECKLIST_KEYS = [
  // "gps",
  // "configuration",
  // "dialup_number",
  // "disconnect_f1_f2",
  // "pause_notifications",
  // "test_alarm_performed",
  // "reenable_notifications",
  // "add_sms",
  // "additional_services",
  // "pack_correct_accessories",
  // "pack_quick_guide",
];

// Per-device-type checklists. To add a device type: append a group here and
// add its labels to the locale files — nothing else changes, in either repo.
//   key:      group id; stored checklist keys become `<group key>.<item>`;
//             group title label: order_log.device_types.<key>
//   match:    product.type prefixes (first matching group wins, so keep
//             specific prefixes like ajax_hub before the generic ajax)
//   progress: items for the edit-order checklist (REQ-05)
//             [] = not defined yet — such products fall back to the
//             general list and are flagged to support
//   item labels: order_log.device_checklist_items.<item>, falling back to
//             order_log.progress_checklist_items.<item> for shared items
export const DEVICE_CHECKLIST_GROUPS = [
  {
    key: "twig_neo_one_srt405_406",
    match: ["twig_neo", "twig_one", "srt405", "srt406"],
    progress: [
      "gps",
      "configuration",
      "dialup_number",
      "disconnect_f1_f2",
      "pause_notifications",
      "test_alarm_performed",
      "reenable_notifications",
      "add_sms",
      "additional_services",
      "pack_correct_accessories",
      "pack_quick_guide",
    ],
  },
  {
    key: "srt334_twig_beacon",
    match: ["srt334", "twig_beacon"],
    progress: ["product_added_securcloud", "reports_to_securcloud", "packed_charger_accessories"],
  },
  {
    key: "emerit_watch",
    match: ["emerit_wg"],
    progress: [
      "configured_emerit_portal",
      "added_securcloud",
      "pause_notifications",
      "test_alarm_performed",
      "reenable_notifications",
      "add_sms",
      "pack_correct_accessories",
      "pack_quick_guide",
    ],
  },
  {
    key: "emerit_e_b10",
    match: ["emerit_e_b10"],
    progress: [
      "configured_correct_template",
      "dialup_number_added",
      "added_securcloud",
      "tested_emerit_app",
      "reports_in_securcloud",
      "pause_sms_notifications",
      "test_alarm_against",
      "reenable_notifications",
      "add_sms",
      "additional_services",
      "pack_correct_accessories",
      "pack_quick_guide",
    ],
  },
  {
    key: "ajax_hub",
    match: ["ajax_hub"],
    progress: [
      "added_securcloud",
      "added_ajax_portal",
      "reports_ajax_portal",
      "connect_products_securcloud",
      "connect_products_ajax_portal",
    ],
  },
  {
    key: "ajax_other",
    match: ["ajax"],
    progress: ["sales_add_ajax_products", "ajax_portal_informed"],
  },
  {
    key: "srt430",
    match: ["srt430"],
    progress: [
      "product_added_securcloud",
      "configuration_added",
      "reports_to_securcloud",
      "tested",
      "alarms_connected_new_existing",
      "packed_charger_accessories",
      "mounting_guide_packed",
    ],
  },
];

const NON_DEVICE_CATEGORIES = ["software", "simcard", "accessory"];
const NON_DEVICE_TYPE_PREFIXES = [
  "securcloud",
  "securone",
  "securtool",
  "simcard",
  "addon",
  "other",
  "larmcentral",
];

function isDeviceProduct(product) {
  if (!product || !(product.qty > 0)) return false;
  if (product.is_software) return false;
  if (NON_DEVICE_CATEGORIES.includes(product.category)) return false;
  const type = String(product.type || "").toLowerCase();
  if (!type) return false;
  return !NON_DEVICE_TYPE_PREFIXES.some((prefix) => type.startsWith(prefix));
}

// kind: 'progress'
// -> { groups, unmatched } for the device types on the order. A device
// product whose group has no items for this checklist kind counts as
// unmatched (flagged to support).
export function deviceChecklistsForProducts(products, kind) {
  const groupKeys = new Set();
  const unmatched = new Set();
  (products || []).forEach((product) => {
    if (!isDeviceProduct(product)) return;
    const type = String(product.type || "").toLowerCase();
    const group = DEVICE_CHECKLIST_GROUPS.find((g) =>
      g.match.some((prefix) => type.startsWith(prefix))
    );
    if (group && (group[kind] || []).length) {
      groupKeys.add(group.key);
    } else {
      unmatched.add(product.display_name || product.name || product.type);
    }
  });
  return {
    groups: DEVICE_CHECKLIST_GROUPS.filter((g) => groupKeys.has(g.key)),
    unmatched: [...unmatched],
  };
}

// The sections a checklist view renders for an order:
//   general: the general item keys (always shown)
//   groups:  matched device groups (render group[kind] items in addition)
//   unmatched: device names with no defined items, flagged to support
export function checklistSectionsForProducts(products, kind) {
  const { groups, unmatched } = deviceChecklistsForProducts(products, kind);
  return {
    general: PROGRESS_CHECKLIST_KEYS,
    groups,
    unmatched,
  };
}
